import {
    DAY_COLUMN_KEY,
    MONTH_COLUMN_KEY,
    QUARTER_COLUMN_KEY,
    YEAR_COLUMN_KEY,
} from 'business/constants';
import { SerieT } from 'components/ChartContainer/components/Chart/types';
import { DAYS, MONTHS, QUARTERS } from 'helpers/dateHelper';
import {
    sortDayNames,
    sortMonthNames,
    sortQuarterNames,
} from 'helpers/helpers';
import { FilterOperations, TimeScales } from 'business/definitions';
import { YearlyComparisonScale } from './scale';

const uniqueQuarterNames = Array.from(new Set(QUARTERS));

const parseMonthValue = (date: Date) => {
    const month = date.getMonth();
    return MONTHS[month];
};
const parseDayValue = (date: Date) => {
    const month = date.getMonth();
    return `${date.getDate()} ${MONTHS[month]}`;
};
const parseQuarterValue = (date: Date) => {
    const month = date.getMonth();
    return QUARTERS[month];
};

/**
 * This function returns the filter value for a quarter
 */
const getQuarterFilterValue = (quarter: string, year: string): string => {
    switch (quarter) {
        case 'T1':
            return `${year}-01-01|${year}-03-31`;
        case 'T2':
            return `${year}-04-01|${year}-06-30`;
        case 'T3':
            return `${year}-07-01|${year}-09-30`;
        case 'T4':
            return `${year}-10-01|${year}-12-31`;
        default:
            throw new Error(`Unknown quarter: ${quarter}`);
    }
};

/**
 * Extracts the date from a day string and returns it in the format 'YYYY-MM-DD'
 */
const extractDayFilterFromPoint = (
    day: string,
    point: ResponseDataT,
): string => {
    const components = day.split(' ');
    const dayNumber = components[0].padStart(2, '0');
    const monthNumber = (MONTHS.indexOf(components[1]) + 1)
        .toString()
        .padStart(2, '0');
    const year = point[YEAR_COLUMN_KEY];

    const date = `${year}-${monthNumber}-${dayNumber}`;
    return date;
};

const getYearlyComparisonAxisData = (
    allData: string[],
    graphSeries: SerieT[],
    sortDateNames: (a: string, b: string) => number,
    isXorY: 'x' | 'y',
) => {
    const serieDataIndex = isXorY === 'x' ? 0 : 1;
    const xAxisData = graphSeries
        .flatMap((serie) => serie.data.map((datum) => datum[serieDataIndex]))
        .sort(sortDateNames);
    const firstIndex = allData.indexOf(String(xAxisData[0]));
    const lastIndex = allData.indexOf(String(xAxisData[xAxisData.length - 1]));
    return allData.slice(firstIndex, lastIndex + 1);
};

export type YearlyComparisonSettings = {
    parseValue: (date: Date) => string;
    values: string[];
    getCurrentValue: (date: Date) => string;
    sort: (a: string, b: string) => number;
    getXAxisData: (series: SerieT[]) => string[];
    getYAxisData: (series: SerieT[]) => string[];
    getDetailFilter: (
        point: ResponseDataT,
        dimension: QueryDimensionT,
    ) => QueryFilterT[];
};

export const yearlyComparisonSettingsForScale: Record<
    YearlyComparisonScale,
    YearlyComparisonSettings
> = {
    [MONTH_COLUMN_KEY]: {
        parseValue: parseMonthValue,
        values: MONTHS,
        getCurrentValue: (date) => MONTHS[date.getMonth()],
        sort: sortMonthNames,
        getXAxisData: (series: SerieT[]) =>
            getYearlyComparisonAxisData(MONTHS, series, sortMonthNames, 'x'),
        getYAxisData: (series: SerieT[]) =>
            getYearlyComparisonAxisData(MONTHS, series, sortMonthNames, 'y'),
        getDetailFilter: (point: ResponseDataT, dimension: QueryDimensionT) => {
            if (point[MONTH_COLUMN_KEY] === null) return [];
            const pointScaleValueIndex =
                MONTHS.indexOf(point[MONTH_COLUMN_KEY].toString()) + 1;
            const year = point[YEAR_COLUMN_KEY];
            return [
                {
                    field: dimension.field,
                    operation: FilterOperations.extract,
                    value: pointScaleValueIndex.toString(),
                    valueScale: TimeScales.month,
                },
                {
                    field: dimension.field,
                    operation: FilterOperations.between,
                    value: `${year}-01-01|${year}-12-31`,
                },
            ];
        },
    },
    [DAY_COLUMN_KEY]: {
        parseValue: parseDayValue,
        values: DAYS,
        getCurrentValue: (date: Date) => {
            const month = date.getMonth();
            return `${date.getDate() + 1} ${MONTHS[month]}`;
        },
        sort: sortDayNames,
        getXAxisData: (series: SerieT[]) =>
            getYearlyComparisonAxisData(DAYS, series, sortDayNames, 'x'),
        getYAxisData: (series: SerieT[]) =>
            getYearlyComparisonAxisData(DAYS, series, sortDayNames, 'y'),
        getDetailFilter: (point: ResponseDataT, dimension: QueryDimensionT) => {
            const day = point[DAY_COLUMN_KEY] as string;

            const date = extractDayFilterFromPoint(day, point);

            return [
                {
                    field: dimension.field,
                    operation: FilterOperations.between,
                    value: `${date}|${date}`,
                },
            ];
        },
    },
    [QUARTER_COLUMN_KEY]: {
        parseValue: parseQuarterValue,
        values: uniqueQuarterNames,
        getCurrentValue: (date: Date) => QUARTERS[date.getMonth()],
        sort: sortQuarterNames,
        getXAxisData: (series: SerieT[]) =>
            getYearlyComparisonAxisData(
                uniqueQuarterNames,
                series,
                sortQuarterNames,
                'x',
            ),
        getYAxisData: (series: SerieT[]) =>
            getYearlyComparisonAxisData(
                uniqueQuarterNames,
                series,
                sortQuarterNames,
                'y',
            ),
        getDetailFilter: (point: ResponseDataT, dimension: QueryDimensionT) => [
            {
                field: dimension.field,
                operation: FilterOperations.between,
                value: getQuarterFilterValue(
                    point[QUARTER_COLUMN_KEY] as string,
                    point[YEAR_COLUMN_KEY] as string,
                ),
            },
        ],
    },
};

export const getYearlyComparisonSettingsForScale = (
    scale: YearlyComparisonScale,
) => yearlyComparisonSettingsForScale[scale];

/**
 * This function returns the max scale value index in the whole dataset.
 * Example:
 * Data: [
 * {month: "Jan", year: 2019},
 * {month: "Fév", year: 2019},
 * {month: "Mar", year: 2019},
 * {month: "Jan", year: 2020},
 * {month: "Fév", year: 2020},
 * {month: "Mar", year: 2020},
 * {month: "Jan", year: 2021},
 * {month: "Fév", year: 2021},
 * ]
 * Returns: The last month is March so we return 2
 */
const getMaxScaleValueIndex = (
    possibleScaleValues: string[],
    dataScaleValues: ResponseDataValueT[],
    yearlyComparisonSettings: YearlyComparisonSettings,
): number => {
    const sortedDataScaleValues = dataScaleValues.sort(
        yearlyComparisonSettings.sort,
    );
    const lastDataScaleValueIndex = possibleScaleValues.indexOf(
        sortedDataScaleValues[sortedDataScaleValues.length - 1] as string,
    );
    return lastDataScaleValueIndex;
};

const getCurrentScaleValueIndex = (
    possibleScaleValues: string[],
    yearlyComparisonSettings: YearlyComparisonSettings,
    maxDate: Date,
): number => {
    const currentScaleValue = yearlyComparisonSettings.getCurrentValue(maxDate);
    const currentScaleValueIndex =
        possibleScaleValues.indexOf(currentScaleValue);
    return currentScaleValueIndex;
};

const getAdditionalDimensions = (
    dataGroup: ResponseDataT[],
    additionalColumns: ResponseColumnT[],
) => {
    const dimensionKeys = additionalColumns.map((d) => d.key);
    const additionalDimensions = dimensionKeys.reduce(
        (acc, key) => ({ ...acc, [key]: dataGroup[0][key] }),
        {},
    );
    return additionalDimensions;
};

export const fillGapInYearlyPivotedData = (
    dataGroup: ResponseDataT[],
    scale: YearlyComparisonScale,
    additionalColumns: ResponseColumnT[],
    dataScaleValues: ResponseDataValueT[],
    maxDate: Date,
) => {
    // This is a yearly comparison. Yearly comparaison only works for data of type months, days or quarters
    // and the type of the xAxis being a category (despite dealing with dates)
    // It's different than time series since data looks like this :
    // {
    //  2019: [
    //    {"budgetLine.amountMandated": 8751.5, month: "Fév", year: 2019,}
    //    {"budgetLine.amountMandated": 3751.5, month: "Jun", year: 2019}
    //    ]
    // }
    //  2020: [
    //    {"budgetLine.amountMandated": 10751.5, month: "Jan", year: 2020,}
    //    {"budgetLine.amountMandated": 351.5, month: "Mai", year: 2020}
    //    ]
    // }
    // We add dummy data to months that don't have any value.
    const { year } = dataGroup[0] as { year: number };

    const currentYear = new Date().getFullYear();
    const isPastYear = year < currentYear;

    const yearlyComparisonSettings = getYearlyComparisonSettingsForScale(scale);
    const possibleScaleValues = yearlyComparisonSettings.values;

    const currentScaleValues = dataGroup.map((dg) => dg[scale]);

    // We want to determine the last month/day/quarter in the dataset
    // We use to get the last month/day/quarter in the dataset group
    const lastScaleValueIndex = getMaxScaleValueIndex(
        possibleScaleValues,
        dataScaleValues,
        yearlyComparisonSettings,
    );

    // We do not want to add data for the month/day/quarter that are in the future
    const currentScaleValueIndex = getCurrentScaleValueIndex(
        possibleScaleValues,
        yearlyComparisonSettings,
        maxDate,
    );
    const maxScaleValueIndex = !isPastYear
        ? Math.min(lastScaleValueIndex, currentScaleValueIndex)
        : lastScaleValueIndex;

    const scaleValuesToAdd = possibleScaleValues
        .slice(0, maxScaleValueIndex + 1)
        .filter((m) => !currentScaleValues.includes(m));

    if (scaleValuesToAdd) {
        const additionalDimensions = getAdditionalDimensions(
            dataGroup,
            additionalColumns,
        );
        scaleValuesToAdd.forEach((value) => {
            dataGroup.push({ ...additionalDimensions, [scale]: value, year });
        });
    }
    return dataGroup;
};
